import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SERVER_BASE_URL } from '../services/api';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Radio as RadioIcon,
  Check as CheckIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { radioAPI } from '../services/api';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const RadioChannelEditPage = () => {
  const { playlistId, channelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [playlist, setPlaylist] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    icon: '',
    stream: '',
    description: '',
    text: '',
    epg: '',
    sort_order: 1,
    geo_restrictions: [],
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNew = !channelId || channelId === 'new';
  const returnPlaylistId = searchParams.get('playlistId') || playlistId;

  useEffect(() => {
    if (playlistId) {
      loadPlaylist();
    }
    if (!isNew && channelId && playlistId) {
      loadChannel();
    } else if (isNew && returnPlaylistId) {
      // Для нового канала устанавливаем следующий sort_order
      loadNextSortOrder();
    }
  }, [channelId, playlistId]);

  const loadPlaylist = async () => {
    try {
      const response = await radioAPI.getPlaylistById(playlistId);
      setPlaylist(response.data);
    } catch (error) {
      console.error('Ошибка загрузки плейлиста:', error);
    }
  };

  const loadChannel = async () => {
    try {
      setLoading(true);
      const response = await radioAPI.getPlaylistChannel(playlistId, channelId);
      setFormData({
        url: response.data.url || '',
        name: response.data.name || '',
        icon: response.data.icon || '',
        stream: response.data.stream || '',
        description: response.data.description || '',
        text: response.data.text || '',
        epg: response.data.epg || '',
        sort_order: response.data.sort_order || 1,
        geo_restrictions: (() => {
          const geoData = response.data.geo_restrictions;
          if (Array.isArray(geoData)) {
            // Фильтруем только строковые значения
            return geoData.filter(item => typeof item === 'string');
          }
          if (typeof geoData === 'string') {
            return [geoData];
          }
          return [];
        })(),
        is_active: response.data.is_active ?? true
      });
    } catch (error) {
      console.error('Ошибка загрузки канала:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка загрузки канала',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadNextSortOrder = async () => {
    try {
      const response = await radioAPI.getPlaylistChannels(returnPlaylistId);
      const channels = response.data;
      const maxSortOrder = channels.length > 0 ? Math.max(...channels.map(c => c.sort_order)) : 0;
      setFormData(prev => ({ ...prev, sort_order: maxSortOrder + 1 }));
    } catch (error) {
      console.error('Ошибка определения позиции:', error);
    }
  };

  const handleIconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Проверяем тип файла
    if (!file.type.startsWith('image/')) {
      setSnackbar({
        open: true,
        message: 'Пожалуйста, выберите файл изображения',
        severity: 'error'
      });
      return;
    }

    // Проверяем размер файла (максимум 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'Размер файла не должен превышать 5MB',
        severity: 'error'
      });
      return;
    }

    try {
      setUploadingIcon(true);
      const response = await radioAPI.uploadIcon(file);
      const iconUrl = `${SERVER_BASE_URL}${response.data.icon_url}`;
      
      setFormData(prev => ({ ...prev, icon: iconUrl }));
      setSnackbar({
        open: true,
        message: 'Иконка успешно загружена',
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка загрузки иконки:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка загрузки иконки',
        severity: 'error'
      });
    } finally {
      setUploadingIcon(false);
      // Очищаем input
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isNew) {
        // Создаем новый канал
        const channelResponse = await radioAPI.createChannel(formData);
        const newChannelId = channelResponse.data.id;
        
        // Добавляем в плейлист, если указан
        if (returnPlaylistId) {
          await radioAPI.addChannelToPlaylist(returnPlaylistId, newChannelId, formData.sort_order);
        }
        
        setSnackbar({
          open: true,
          message: 'Радио канал создан',
          severity: 'success'
        });
        
        // Переходим к редактированию созданного канала
        if (returnPlaylistId) {
          navigate(`/radio/playlists/${returnPlaylistId}/channels/${newChannelId}/edit`, { replace: true });
        } else {
          navigate('/radio/channels');
        }
      } else {
        // Обновляем существующий канал
        await radioAPI.updateChannelInPlaylist(playlistId || returnPlaylistId, channelId, formData);
        
        setSnackbar({
          open: true,
          message: 'Радио канал обновлен',
          severity: 'success'
        });
        
        // Показываем индикатор успешного сохранения
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (error) {
      console.error('Ошибка сохранения канала:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка сохранения канала',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (returnPlaylistId) {
      navigate(`/radio/playlists/${returnPlaylistId}/channels`);
    } else if (playlistId) {
      navigate(`/radio/playlists/${playlistId}/channels`);
    } else {
      navigate('/radio/playlists');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (saved) setSaved(false); // Сбрасываем индикатор при изменении
  };

  const countries = [
    'RU', 'BY', 'KZ', 'UA', 'US', 'GB', 'DE', 'FR', 'IT', 'ES', 'CN', 'JP', 'KR', 'IN'
  ];

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RadioIcon />
            {isNew ? 'Создать радио канал' : 'Редактировать радио канал'}
          </Typography>
          {playlist && (
            <Typography variant="body2" color="text.secondary">
              Плейлист: {playlist.name}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={saved ? <CheckIcon /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          color={saved ? 'success' : 'primary'}
          size={isMobile ? 'small' : 'medium'}
        >
          {saving ? 'Сохранение...' : saved ? '✓ Сохранено' : 'Сохранить'}
        </Button>
      </Box>

      <Paper sx={{ width: '100%' }}>
        {/* Табы */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant={isMobile ? 'fullWidth' : 'standard'}
            scrollButtons="auto"
          >
            <Tab label="Основные" />
            <Tab label="Поток" />
            <Tab label="Дополнительно" />
          </Tabs>
        </Box>

        {/* Основные настройки */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="URL канала"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              fullWidth
              required
              placeholder="https://example.com/radio/stream"
            />
            
            <TextField
              label="Название канала"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Описание"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="URL иконки"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                fullWidth
                placeholder="https://example.com/icon.png"
                helperText="Введите URL иконки или загрузите файл"
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 'fit-content' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="icon-upload-input"
                  type="file"
                  onChange={handleIconUpload}
                />
                <label htmlFor="icon-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={uploadingIcon}
                    startIcon={uploadingIcon ? <ImageIcon /> : <CloudUploadIcon />}
                    sx={{ minWidth: 120 }}
                  >
                    {uploadingIcon ? 'Загрузка...' : 'Загрузить'}
                  </Button>
                </label>
                {formData.icon && (
                  <Box
                    component="img"
                    src={formData.icon}
                    alt="Превью иконки"
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </Box>
            </Box>

            <TextField
              label="Позиция в плейлисте"
              type="number"
              value={formData.sort_order}
              onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
              fullWidth
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                />
              }
              label="Активен"
            />
          </Box>
        </TabPanel>

        {/* Настройки потока */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Stream URL"
              value={formData.stream}
              onChange={(e) => handleInputChange('stream', e.target.value)}
              fullWidth
              placeholder="https://example.com/radio/stream.m3u8"
              helperText="Прямая ссылка на поток (если отличается от основного URL)"
            />
            
            <TextField
              label="Текст канала"
              value={formData.text}
              onChange={(e) => handleInputChange('text', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Дополнительная информация о канале"
            />
            
            <TextField
              label="EPG URL"
              value={formData.epg}
              onChange={(e) => handleInputChange('epg', e.target.value)}
              fullWidth
              placeholder="https://example.com/epg.xml"
              helperText="Ссылка на программу передач (если доступна)"
            />
          </Box>
        </TabPanel>

        {/* Дополнительные настройки */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Гео-ограничения</InputLabel>
              <Select
                multiple
                value={Array.isArray(formData.geo_restrictions) ? formData.geo_restrictions : []}
                onChange={(e) => handleInputChange('geo_restrictions', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Array.isArray(selected) ? selected.map((value, index) => {
                      // Убеждаемся, что value является строкой
                      const displayValue = typeof value === 'string' ? value : 
                        (typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value));
                      return (
                        <Chip key={index} label={displayValue} size="small" />
                      );
                    }) : []}
                  </Box>
                )}
              >
                <MenuItem value="WORLD">
                  <em>Весь мир</em>
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Превью данных */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Превью данных канала
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <pre style={{ 
                  margin: 0, 
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify({
                    url: formData.url,
                    name: formData.name,
                    icon: formData.icon,
                    stream: formData.stream,
                    description: formData.description,
                    text: formData.text,
                    epg: formData.epg,
                    sort_order: formData.sort_order,
                    geo_restrictions: formData.geo_restrictions,
                    is_active: formData.is_active
                  }, null, 2)}
                </pre>
              </Paper>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RadioChannelEditPage;
